import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import localeService from 'src/dataServices/LocaleService';
import { IApplicationState } from 'src/state';
import { createSupportTicket } from 'src/state/contactus/actions';
import { IContactUsSubmissionFields } from 'src/state/contactus/types';
import './TicketProposal.scss';
import { TicketTextField } from './TicketTextField';
import { ITicketDraft } from './types';

interface TicketFormProps {
  data: ITicketDraft;
  actionsEnabled: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  onError: () => void;
}

// Native validation needs a message string; `true` falls back to the browser's default text.
const requiredRule = (errorKey: string) => ({
  required: localeService.getText('ContactUs', errorKey) || true
});

// `type="tel"` validates nothing, so constrain the format: at least 7 digits, and only digits
// plus the usual separators. `(`, `)` and `-` are escaped because browsers compile the emitted
// `pattern` attribute with the RegExp `v` flag, which rejects them unescaped inside a class.
const PHONE_PATTERN = /^(?=(?:\D*\d){7,})\+?[\d\s\(\)\.\-]+$/;

// `type="email"` accepts a bare host such as `someone@intranet`; require a dotted domain on top.
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

// The API names fields after its DTO properties (`UserEmail`); the form uses camelCase.
const toFieldName = (apiField: string) => apiField.charAt(0).toLowerCase() + apiField.slice(1);

// Synchronously hide all old chat messages from screen readers before NVDA
// builds its virtual buffer, then focus the summary element so NVDA reads
// only the form overview. On unmount, restore them.
function useHideChatHistoryFromScreenReader() {
  const summaryRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrappers = document.querySelectorAll('.copilot-message-wrapper');
    const lastIndex = wrappers.length - 1;
    const hidden: Element[] = [];
    let lastWrapper: Element | null = null;

    const lastWrapperOriginalAttributes: { role: string | null; ariaLabel: string | null } = {
      role: null,
      ariaLabel: null
    };

    wrappers.forEach((el, i) => {
      if (i < lastIndex) {
        el.setAttribute('aria-hidden', 'true');
        hidden.push(el);
      } else {
        // Store original role and aria-label values before removing them
        lastWrapper = el;
        lastWrapperOriginalAttributes.role = el.getAttribute('role');
        lastWrapperOriginalAttributes.ariaLabel = el.getAttribute('aria-label');
        el.removeAttribute('role');
        el.removeAttribute('aria-label');
      }
    });

    const timer = setTimeout(() => {
      summaryRef.current?.focus();
    }, 200);

    return () => {
      clearTimeout(timer);
      hidden.forEach((el) => el.removeAttribute('aria-hidden'));
      if (lastWrapper) {
        // Restore original role and aria-label values
        if (lastWrapperOriginalAttributes.role) {
          (lastWrapper as Element).setAttribute('role', lastWrapperOriginalAttributes.role);
        }
        if (lastWrapperOriginalAttributes.ariaLabel) {
          (lastWrapper as Element).setAttribute('aria-label', lastWrapperOriginalAttributes.ariaLabel);
        }
      }
    };
  }, []);
  return summaryRef;
}

export function TicketForm({ data, actionsEnabled, onSubmit, onCancel, onError }: TicketFormProps) {
  const contactUs = useSelector((state: IApplicationState) => state.contactUs);
  const countryCodes = contactUs.countryCodes || [];
  const dispatch = useDispatch();

  const { register, handleSubmit } = useForm<IContactUsSubmissionFields>({
    mode: 'onSubmit',
    shouldUseNativeValidation: true,
    progressive: true,
    defaultValues: data.ticket
  });

  // The summary below is announced once on mount, so the draft values are all it ever needs.
  const { userFirstName, userLastName, userEmail, issue, nonprofitName, userPhone, countryCode } = data.ticket;
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Native `:invalid` only styles fields once the user has tried to submit.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const summaryRef = useHideChatHistoryFromScreenReader();
  const formRef = useRef<HTMLFormElement>(null);

  // Handle the support ticket API response with the submit-time values.
  useEffect(() => {
    if (!isSubmitting) return;

    if (contactUs.supportTicketResponse?.Id) {
      setIsSubmitting(false);
      onSubmit();
      return;
    }

    if (contactUs.supportTicketFieldErrors) {
      setIsSubmitting(false);
      const form = formRef.current;
      let reported = false;

      Object.entries(contactUs.supportTicketFieldErrors).forEach(([apiField, message]) => {
        // Fields the form does not render (Category, Role, UserLocale) have no control to find.
        const field = form?.elements.namedItem(toFieldName(apiField));
        if (
          field instanceof HTMLInputElement ||
          field instanceof HTMLSelectElement ||
          field instanceof HTMLTextAreaElement
        ) {
          field.setCustomValidity(message);
          reported = true;
        }
      });

      // Nothing the user can fix on this form, so fall back to the generic failure card.
      if (reported) form?.reportValidity();
      else onError();
      return;
    }

    if (contactUs.failedToSend) {
      setIsSubmitting(false);
      onError();
    }
  }, [
    isSubmitting,
    contactUs.supportTicketResponse,
    contactUs.supportTicketFieldErrors,
    contactUs.failedToSend,
    onSubmit,
    onError
  ]);

  const submitTicket = (ticket: IContactUsSubmissionFields) => {
    setIsSubmitting(true);

    try {
      dispatch(createSupportTicket(ticket, data.agentMetadata));
    } catch (error) {
      setIsSubmitting(false);
      onError();
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(submitTicket)}
      className={`support-ticket-card support-ticket-card--form${
        submitAttempted ? ' support-ticket-card--validated' : ''
      }`}
    >
      <h3 className="support-ticket-form-title" id="ticket-form-title">
        {localeService.getText('ContactUs', 'TicketFormTitle')}
      </h3>
      <div
        ref={summaryRef}
        tabIndex={-1}
        className="copilot-sr-only"
        aria-label={`${localeService.getText('ContactUs', 'TicketFormTitle')}. First name: ${
          userFirstName || 'empty'
        }. Last name: ${userLastName || 'empty'}. Country code: ${countryCode || 'empty'}. Phone number: ${
          userPhone || 'empty'
        }. Contact email: ${userEmail || 'empty'}. Organization name: ${nonprofitName || 'empty'}. How can we help: ${
          issue || 'empty'
        }. ${localeService.getText('ContactUs', 'CancelButton')} button. ${localeService.getText(
          'ContactUs',
          'TicketSubmitRequest'
        )} button.`}
      />

      <div className="support-ticket-field-row">
        <TicketTextField
          id="firstName"
          label={localeService.getText('ContactUs', 'FirstName')}
          {...register('userFirstName', requiredRule('userFirstNameRequiredError'))}
        />
        <TicketTextField
          id="lastName"
          label={localeService.getText('ContactUs', 'LastName')}
          {...register('userLastName', requiredRule('userLastNameRequiredError'))}
        />
      </div>

      <div className="support-ticket-field-group">
        <label htmlFor="countryCode" className="support-ticket-label">
          <span aria-hidden="true">*</span> {localeService.getText('ContactUs', 'CountryPlaceholder')}
        </label>
        <select
          id="countryCode"
          {...register('countryCode', requiredRule('countryRequiredError'))}
          className="support-ticket-input support-ticket-select"
        >
          <option value="">{localeService.getText('ContactUs', 'SelectAnOption')}</option>
          {countryCodes.map((cc) => (
            <option key={cc.code} value={cc.code}>{`${cc.name} (${cc.code})`}</option>
          ))}
        </select>
      </div>

      <div className="support-ticket-field-row">
        <TicketTextField
          id="phone"
          type="tel"
          label={localeService.getText('ContactUs', 'PhoneNumber')}
          {...register('userPhone', { ...requiredRule('userPhoneRequiredError'), pattern: PHONE_PATTERN })}
        />
        <TicketTextField
          id="email"
          type="email"
          label={localeService.getText('ContactUs', 'UserEmail')}
          {...register('userEmail', { ...requiredRule('userEmailRequiredError'), pattern: EMAIL_PATTERN })}
        />
      </div>

      <TicketTextField
        id="nonprofitName"
        label={localeService.getText('ContactUs', 'NonprofitName')}
        {...register('nonprofitName', requiredRule('nonprofitNameRequiredError'))}
      />

      <div className="support-ticket-field-group support-ticket-field-group--last">
        <label htmlFor="question" className="support-ticket-label">
          <span aria-hidden="true">*</span> {localeService.getText('ContactUs', 'HowCanWeHelp')}
        </label>
        <textarea
          id="question"
          {...register('issue', requiredRule('issueRequiredError'))}
          rows={3}
          className="support-ticket-input support-ticket-textarea"
          placeholder={localeService.getLocaleContent().ticketQuestionPlaceholder}
        />
      </div>

      <div className="support-ticket-actions support-ticket-actions--end">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting || !actionsEnabled}
          className="support-ticket-button support-ticket-button--secondary"
        >
          {localeService.getText('ContactUs', 'CancelButton')}
        </button>
        <button
          type="submit"
          onClick={() => setSubmitAttempted(true)}
          disabled={isSubmitting || !actionsEnabled}
          className="support-ticket-button support-ticket-button--primary"
        >
          {isSubmitting ? (
            <>
              <svg className="support-ticket-spinner" viewBox="0 0 24 24">
                <circle
                  className="support-ticket-spinner-track"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="support-ticket-spinner-head"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              {localeService.getText('ContactUs', 'SubmittingButton')}
            </>
          ) : (
            localeService.getText('ContactUs', 'TicketSubmitRequest')
          )}
        </button>
      </div>
    </form>
  );
}

